// src/pages/DocumentsTestPage.js
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Folder as FolderIcon,
  Lock as LockIcon,
  Share as ShareIcon,
  Description as DescriptionIcon,
  FolderShared as FolderSharedIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { PersonalDocuments, Vault, SharedDocuments } from '../Components/Documents/index';
import FamilyDocumentsTab from '../Components/Documents/FamilyDocumentsTab';
import FamilyVaultTab from '../Components/Documents/FamilyVaultTab';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { isFamilyAdmin } from '../services/familyDocumentService';

/**
 * DocumentsTestPage
 *
 * Document management system with two-tier family architecture
 *
 * For Regular Members (4 tabs):
 * 1. Personal Documents - User's private documents
 * 2. Family Documents - Admin-curated resources (view only)
 * 3. Vault - Personal secure documents with sharing
 * 4. Shared With Me - Documents shared by others
 *
 * For Family Admins (5 tabs):
 * 1. Personal Documents - User's private documents
 * 2. Family Documents - Admin-curated resources (can upload/manage)
 * 3. Vault - Personal secure documents with sharing
 * 4. Family Vault - Admin-only vault with granular access control
 * 5. Shared With Me - Documents shared by others
 */
const DocumentsTestPage = () => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const [searchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Check if user is family admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (currentUser && profile?.familyCode) {
        const adminStatus = await isFamilyAdmin(profile.familyCode, currentUser.uid);
        setIsAdmin(adminStatus);
      }
    };
    checkAdminStatus();
  }, [currentUser, profile]);

  // Map tab query parameter to tab index and update when isAdmin changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabMap = {
        'personal': 0,
        'familyDocuments': 1,
        'vault': 2,
        'familyVault': 3,
        'shared': isAdmin ? 4 : 3
      };
      const newTab = tabMap[tabParam];
      if (newTab !== undefined) {
        setCurrentTab(newTab);
      }
    }
  }, [searchParams, isAdmin]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Please sign in to access documents.
        </Alert>
      </Box>
    );
  }

  if (!profile?.familyCode) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Please join a family to access document management.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <DescriptionIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h4" gutterBottom>
            Document Management
          </Typography>
        </Box>

</Box>

      <Divider sx={{ mb: 3 }} />

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 2 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: { xs: 48, sm: 64 },
              textTransform: 'none',
              fontSize: { xs: '0.8rem', sm: '1rem' },
              minWidth: { xs: 100, sm: 'auto' },
            }
          }}
        >
          <Tab icon={<FolderIcon />} iconPosition="start"
            label={isMobile ? 'Personal' : <Box><Typography variant="subtitle1">Personal Documents</Typography><Typography variant="caption" color="text.secondary">Your private files</Typography></Box>}
          />
          <Tab icon={<FolderSharedIcon />} iconPosition="start"
            label={isMobile ? 'Family' : <Box><Typography variant="subtitle1">Family Documents</Typography><Typography variant="caption" color="text.secondary">{isAdmin ? 'Admin-curated resources' : 'Resources for all'}</Typography></Box>}
          />
          <Tab icon={<LockIcon />} iconPosition="start"
            label={isMobile ? 'Vault' : <Box><Typography variant="subtitle1">Vault</Typography><Typography variant="caption" color="text.secondary">Secure & shareable</Typography></Box>}
          />
          {isAdmin && (
            <Tab icon={<SecurityIcon />} iconPosition="start"
              label={isMobile ? 'Fam. Vault' : <Box><Typography variant="subtitle1">Family Vault</Typography><Typography variant="caption" color="text.secondary">Admin-only access control</Typography></Box>}
            />
          )}
          <Tab icon={<ShareIcon />} iconPosition="start"
            label={isMobile ? 'Shared' : <Box><Typography variant="subtitle1">Shared With Me</Typography><Typography variant="caption" color="text.secondary">Files from family</Typography></Box>}
          />
        </Tabs>

        {/* Tab panels */}
        <Box sx={{ minHeight: '60vh' }}>
          {currentTab === 0 && <PersonalDocuments acceptFileTypes=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.odt,.ods,.odp,.pages,.numbers,.keynote" />}
          {currentTab === 1 && (
            <FamilyDocumentsTab
              storageType="documents"
              acceptFileTypes=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.odt,.ods,.odp"
            />
          )}
          {currentTab === 2 && <Vault />}
          {isAdmin && currentTab === 3 && <FamilyVaultTab storageType="documents" />}
          {currentTab === (isAdmin ? 4 : 3) && <SharedDocuments />}
        </Box>
      </Paper>

    </Box>
  );
};

export default DocumentsTestPage;
